import { IconButton, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  NoDataSkeleton,
  TableBodySkeleton,
} from '../../../common/components';
import { ARTICLE_STOCK_TABLE_TEXTS as TEXTS } from '../constants/inventoryArticlesConstants';
import {
  formatStockQuantity,
  getAvailableStock,
  getWarehouseLabel,
} from '../utils/inventoryArticlesUtils';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
const numberSx = { textAlign: 'right' };
const textSx = { textAlign: 'left' };

const COLUMNS = [
  { key: 'warehouse', label: TEXTS.warehouse, sx: textSx },
  { key: 'onHand', label: TEXTS.onHand, align: 'right', sx: { ...numberSx, width: 140 } },
  { key: 'reserved', label: TEXTS.reserved, align: 'right', sx: { ...numberSx, width: 150 } },
  { key: 'available', label: TEXTS.available, align: 'right', sx: { ...numberSx, width: 150 } },
];

const ACTIONS_COLUMN = { key: 'actions', label: TEXTS.actions, sx: { width: 100 } };

/**
 * Existencia del artículo por depósito, con acceso al ajuste de stock de cada fila.
 * @param {Object} props
 * @param {Array<Object>} props.stocks Existencias del artículo por depósito.
 * @param {Object} props.warehousesByUuid Depósitos indexados por uuid.
 * @param {boolean} props.isLoading Carga en curso.
 * @param {boolean} props.canModifyStock Muestra la columna de acciones.
 * @param {boolean} props.actionsDisabled Bloquea las acciones mientras hay una operación en curso.
 * @param {Function} props.onAdjustStock Abre el ajuste para la fila elegida.
 * @returns {JSX.Element}
 */
const ArticleStockByWarehouseTable = ({
  stocks = [],
  warehousesByUuid = {},
  isLoading = false,
  canModifyStock = false,
  actionsDisabled = false,
  onAdjustStock,
}) => {
  const columns = canModifyStock ? [...COLUMNS, ACTIONS_COLUMN] : COLUMNS;

  return (
    <ClientPaginatedTable rows={stocks} resetKey={stocks[0]?.uuid}>
      {(pageRows) => (
        <GeneralTableBox fixedLayout minTableWidth={640}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <HeadTableCell key={column.key} align={column.align} sx={column.sx}>
                    {column.label}
                  </HeadTableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableBodySkeleton />
              ) : stocks.length === 0 ? (
                <NoDataSkeleton colSpan={columns.length} message={TEXTS.empty} showSubtitle={false} />
              ) : (
                pageRows.map((stock) => {
                  const warehouseLabel = getWarehouseLabel(warehousesByUuid, stock.uuid_warehouse);
                  return (
                    <TableRow key={stock.uuid} hover>
                      <RowTableCell sx={textSx} title={warehouseLabel}>
                        {warehouseLabel}
                      </RowTableCell>
                      <RowTableCell align="right" sx={numberSx}>
                        {formatStockQuantity(stock.quantity_on_hand)}
                      </RowTableCell>
                      <RowTableCell align="right" sx={numberSx}>
                        {formatStockQuantity(stock.quantity_reserved)}
                      </RowTableCell>
                      <RowTableCell align="right" sx={numberSx}>
                        {formatStockQuantity(getAvailableStock(stock))}
                      </RowTableCell>
                      {canModifyStock && (
                        <RowTableCell>
                          <Tooltip title={TEXTS.adjust}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onAdjustStock?.(stock)}
                                disabled={actionsDisabled}
                              >
                                <TuneIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </RowTableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </GeneralTableBox>
      )}
    </ClientPaginatedTable>
  );
};

export default ArticleStockByWarehouseTable;
